import { Pressable, Share, StyleSheet, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { AppText } from '@/components/AppText';
import { CustomDivider } from "@/components/CustomDivider";
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Link } from 'expo-router';
import Footer from '@/components/Footer';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppUtils from '@/utilities/AppUtils';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'expo-router';
import { Image } from 'expo-image';
import { Button } from 'react95-native-rabbl';
import { Gender, getDesiredGenderNames, getGenderName } from '@/types/user_info';
import { AppContext } from '@/components/AppContext';
import { useCallback, useContext, useState } from 'react';
import * as AppUtilities from '@/utilities/AppUtilities';
import { CommunityMembership } from "@/types/community_membership";
import { setData } from '@/state/user';
import { RabblSearchResult } from '@/components/RabblSearchResult';
import { UNKNOWN_COMMUNITY } from '@/types/community';
import { Picker } from '@react-native-picker/picker';
import * as Clipboard from 'expo-clipboard';
import { ExternalLink } from '@/components/ExternalLink';
import { UserRabbls } from '@/components/UserRabbls';
import { Meta } from '@/components/Meta';
import { DisplayMarkdown } from "@/components/DisplayMarkdown";
import { getEthnicities, getReligions } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
  },
  flexRowGapVerticalCenter: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  editImage: {
    width: 32,
    height: 32,
  },
  brandImage: {
    width: 32,
    height: 32,
  },
  indent: {
    paddingLeft: 20,
    gap: 20,
  },
  pickerWrapper: {
  },
  picker: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: "#636363",
    paddingHorizontal: 10,
    paddingVertical: 5,
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  embedContainer: {
    paddingLeft: 20,
    gap: 20,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 20,
  },
});

export default function Settings() {
  const router = useRouter();
  const user_info = useSelector((state: RootState) => state.user.user_info);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const domain = useSelector((state: RootState) => state.user.domain);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const communityMemberships = useSelector((state: RootState) => state.user.communityMemberships);
  const [showHTML, setShowHTML] = useState(false);
  const [embedType, setEmbedType] = useState(1);
  const dispatch = useDispatch();

  const onChangeHidden = useCallback(async() => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "MyProfile.onChangeHidden",
        "/rabbl/api/set_hidden",
        "POST",
        {
          domain: domain,
          hidden: !communityMembership.hidden,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      AppUtils.showInformationalToast(membership.hidden ? "Your profile has been hidden for this Rabbl" : "Your profile has been unhidden for this Rabbl");

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onChangeHidden(), true);
      AppUtilities.handleException(e, "MyProfile.onChangeHidden");
    } finally {
      hideSpinner();
    }
  }, [domain, communityMembership]);

  const logout = async () => {
    await AppUtils.logout(dispatch, showSpinner, hideSpinner);
    router.navigate('/');
  };

  const getMemberLink = useCallback(() => {
    return AppUtils.getCommunityURL(communityMembership.community ?? UNKNOWN_COMMUNITY) + "/r/" + AppUtils.getPublicProfileLinkFragment(communityMembership);
  }, [communityMembership]);

  const getEmbedIframe = useCallback(() => {
    return `<iframe src="${AppUtilities.addCommonQueryParameters(AppUtilities.getActualHostLink(false, false) + "/rabbl/api/embed?uid=" + AppUtils.getPublicProfileLinkFragment(communityMembership), true)}" width="${AppConsts.RABBL_SEARCH_RESULT_WIDTH + AppConsts.EMBED_BOXES_OFFSET + AppConsts.EMBED_BOXES_OFFSET}" height="${AppConsts.RABBL_SEARCH_RESULT_HEIGHT + AppConsts.EMBED_BOXES_OFFSET + AppConsts.EMBED_BOXES_OFFSET}" title="${communityMembership.profile.name + " is on the " + communityMembership.community?.name + " Rabbl"}" frameborder="0" scrolling="no"></iframe>`;
  }, [communityMembership]);

  const getEmbedHTML = useCallback(() => {
    return `<div style="width: ${AppConsts.RABBL_SEARCH_RESULT_WIDTH + AppConsts.EMBED_BOXES_OFFSET + AppConsts.EMBED_BOXES_OFFSET}px; height: ${AppConsts.RABBL_SEARCH_RESULT_HEIGHT + AppConsts.EMBED_BOXES_OFFSET + AppConsts.EMBED_BOXES_OFFSET}px; position: relative;"><a href="${AppUtils.getCommunityURL(communityMembership.community ?? UNKNOWN_COMMUNITY)}/r/${AppUtils.getPublicProfileLinkFragment(communityMembership)}" style="color: ${AppConsts.LINK_COLOR}; font-size: ${AppConsts.EMBED_HTML_FONT_SIZE}px; font-style: italic; text-decoration: none; border-bottom: none; box-shadow: none;"><img src="${AppUtils.getCommunityImageURL(communityMembership.community ?? UNKNOWN_COMMUNITY)}" width="${AppConsts.RABBL_SEARCH_RESULT_WIDTH}" height="${AppConsts.RABBL_SEARCH_RESULT_HEIGHT}" style="line-height: 0; margin-top: ${AppConsts.EMBED_BOXES_OFFSET}px;" /><div style="position: absolute; width: ${AppConsts.RABBL_SEARCH_RESULT_WIDTH}px; top: 0; left: 0; right: 0; margin: 0 auto; text-align: center;"><div style="background-color: ${AppConsts.EMBED_COLOR2}; border: 1px solid #000000; padding-left: 2px; padding-right: 4px; margin-top: 1px; display: inline-flex; gap: 3px; align-items: center; line-height: ${AppConsts.EMBED_HTML_FONT_SIZE}px;"><img src="https://rabbl.com/info/icon_16x16.png" width="16" height="16" style="line-height: 0;" /><div style="max-width: ${AppConsts.RABBL_SEARCH_RESULT_WIDTH - 20}px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;">${communityMembership.community?.name} Rabbl</div></div></div><div style="position: absolute; bottom: 0; right: 0; line-height: 0; width: ${AppConsts.PIP_WIDTH}px; height: ${AppConsts.PIP_HEIGHT}px; border: 1px solid #000000;"><img src="${AppUtilities.getActualHostLink(false, false) + "/rabbl/api/mainvisual?uniqueid=" + AppUtils.getPublicProfileLinkFragment(communityMembership)}" width="${AppConsts.PIP_WIDTH}" height="${AppConsts.PIP_HEIGHT}" style="line-height: 0;" /><div style="position: absolute; width: ${AppConsts.PIP_WIDTH}px; bottom: 0; left: 0; background-color: ${AppConsts.COLOR2}; border-top: 1px solid #000000; display: inline-flex; align-items: center; line-height: ${AppConsts.EMBED_HTML_FONT_SIZE}px; justify-content: center;"><span style="overflow: hidden; white-space: nowrap; text-overflow: ellipsis; padding-left: 2px; padding-right: 2px;">${communityMembership.profile.name + AppUtils.prefixIfNonEmpty(AppUtils.getMemberShortRegion(communityMembership.user_info), " / ")}</span></div></div></a></div>`;
  }, [communityMembership]);

  const copyEmbedToClipboard = useCallback(() => {
    let text = "";
    if (embedType == 1) {
      text = getEmbedIframe();
    } else if (embedType == 2) {
      text = getEmbedHTML();
    }
    Clipboard.setStringAsync(text);
    AppUtils.showInformationalToast("Copied to clilpboard");
  }, [embedType, getEmbedIframe, getEmbedHTML]);

  const copyMemberLinkToClipboard = useCallback(() => {
    let text = getMemberLink();
    Clipboard.setStringAsync(text);
    AppUtils.showInformationalToast("Copied to clilpboard");
  }, [getMemberLink]);

  const share = useCallback(async () => {
    // https://reactnative.dev/docs/next/share
    await Share.share(
      {
        url: getMemberLink()
      },
      {
        subject: communityMembership.profile.name + " is on the " + communityMembership.community?.name + " Rabbl",
      }
    );
  }, [communityMembership]);

  const showCommunitySettings = !isMainLogicalHost && AppUtils.isCommunityMembershipFullyReady(communityMembership);

  return (
    <BasicScrollView center={false}>
      <Meta title={"Settings"} />
      <CustomText>Rabbl settings</CustomText>
      <View style={styles.indent}>
        <AppText><AppText style={CustomTextStyles.highlight}>Email</AppText>: { user_info.email }</AppText>
        { user_info.age_years != -1 && (
            <AppText><AppText style={CustomTextStyles.highlight}>Age</AppText>: { AppUtils.getMemberAgeByUserInfo(user_info) }</AppText>
        )}
        { user_info.gender != -1 && user_info.gender != Gender.GENDER_UNKNOWN && (
            <AppText><AppText style={CustomTextStyles.highlight}>Gender</AppText>: { getGenderName(user_info.gender) }</AppText>
        )}
        { user_info.height != -1 && (
            <AppText><AppText style={CustomTextStyles.highlight}>Height</AppText>: { AppUtils.getHeightHumanReadable(user_info.height) }</AppText>
        )}
        {
          user_info.desired_genders && user_info.desired_genders != Gender.GENDER_UNKNOWN && user_info.desired_genders != -1 && (
            <View style={styles.flexRowGapVerticalCenter}>
              <AppText><AppText style={CustomTextStyles.highlight}>Desired gender(s)</AppText>: { getDesiredGenderNames(user_info.desired_genders ?? 0) }</AppText>
              {
                !isMainLogicalHost && (
                  <>
                    <Link
                      href={{ pathname: '/desires', params: { edit: "y" } }}
                      asChild={AppUtilities.isWeb()}
                    >
                      <Image
                        style={styles.editImage}
                        source={require('@/assets/images/gears32x32.png')}
                      />
                    </Link>
                  </>
                )
              }
            </View>
          )
        }
        <View style={styles.flexRowGapVerticalCenter}>
          <AppText style={CustomTextStyles.highlight}>Manage your notifications</AppText>
          <Link
            href={{ pathname: '/subscriptions', params: { id: user_info.subscriptions_uniqueid } }}
            asChild={AppUtilities.isWeb()}
          >
            <Image
              style={styles.editImage}
              source={require('@/assets/images/gears32x32.png')}
            />
          </Link>
        </View>
        { showCommunitySettings && (
            <View style={styles.flexRowGapVerticalCenter}>
              <AppText><AppText style={CustomTextStyles.highlight}>Allow messages before liking each other</AppText>: {user_info.pref_allow_unmatched_messages ? "Yes" : "No"}</AppText>
              <Link
                href={{ pathname: '/preferences', params: { edit: "y" } }}
                asChild={AppUtilities.isWeb()}
              >
                <Image
                  style={styles.editImage}
                  source={require('@/assets/images/gears32x32.png')}
                />
              </Link>
            </View>
        )}
      </View>

      {
        showCommunitySettings && (
          <>
            <CustomDivider fullWidth={true} />
            <CustomText>Profile settings</CustomText>
            <View style={styles.indent}>
              <AppText>Settings for this <AppText style={CustomTextStyles.highlight}>{communityMembership.community?.name}</AppText> Rabbl:</AppText>
              <View style={styles.flexRowGapVerticalCenter}>
                <AppText><AppText style={CustomTextStyles.highlight}>Profile name</AppText>: { communityMembership.profile.name }</AppText>
                <Link
                  href={{ pathname: '/name', params: { edit: "y" } }}
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              </View>

              <View style={styles.flexRowGapVerticalCenter}>
                <AppText><AppText style={CustomTextStyles.highlight}>Pictures</AppText>:</AppText>
                <Link
                  href={{ pathname: '/visuals', params: { edit: "y" } }}
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              </View>

              <View style={styles.indent}>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.visuals.map((visual) => (
                      <Link
                        href={{ pathname: '/visuals', params: { edit: "y" } }}
                        asChild={AppUtilities.isWeb()}
                        key={visual.uniqueid}
                      >
                        <Image
                          alt={communityMembership.profile.name}
                          style={{
                            width: 128,
                            height: 128,
                          }}
                          contentFit='contain'
                          source={{
                            uri: AppUtils.createBackendLink('/rabbl/api/visual?uniqueid=' + visual.uniqueid)
                          }}
                        />
                      </Link>
                    ))
                  }
                </View>
              </View>

              <View style={styles.flexRowGapVerticalCenter}>
                <AppText><AppText style={CustomTextStyles.highlight}>Location</AppText>: { AppUtils.getMemberShortLocation(communityMembership.user_info) }</AppText>
                <Link
                  href={{ pathname: '/location', params: { edit: "y" } }}
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              </View>

              <View style={styles.flexRowGapVerticalCenter}>
                <AppText><AppText style={CustomTextStyles.highlight}>About you</AppText>:</AppText>
                <Link
                  href={{ pathname: '/aboutyou', params: { edit: "y" } }}
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              </View>

              <View style={styles.indent}>
                <DisplayMarkdown markdown={communityMembership.profile.description} />
              </View>

              <View style={styles.flexRowGapVerticalCenter}>
                <AppText><AppText style={CustomTextStyles.highlight}>Social media links</AppText>:</AppText>
                <Link
                  href={{ pathname: '/socialmedia', params: { edit: "y" } }}
                  asChild={AppUtilities.isWeb()}
                >
                  <Image
                    style={styles.editImage}
                    source={require('@/assets/images/gears32x32.png')}
                  />
                </Link>
              </View>
              <View style={styles.indent}>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.website && communityMembership.profile.website.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getWebsiteLink(communityMembership.profile.website)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/icons/externallink.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getWebsiteLink(communityMembership.profile.website)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.website}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/icons/externallink.png')}
                          />
                          <AppText>Website: Not set</AppText>
                        </>
                  }
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.instagram && communityMembership.profile.instagram.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getInstagramLink(communityMembership.profile.instagram)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/brands/instagramicon.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getInstagramLink(communityMembership.profile.instagram)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.instagram}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/brands/instagramicon.png')}
                          />
                          <AppText>Instagram: Not set</AppText>
                        </>
                  }
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.tiktok && communityMembership.profile.tiktok.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getTikTokLink(communityMembership.profile.tiktok)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/brands/tiktokicon.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getTikTokLink(communityMembership.profile.tiktok)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.tiktok}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/brands/tiktokicon.png')}
                          />
                          <AppText>TikTok: Not set</AppText>
                        </>
                  }
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.snapchat && communityMembership.profile.snapchat.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getSnapchatLink(communityMembership.profile.snapchat)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/brands/snapchaticon.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getSnapchatLink(communityMembership.profile.snapchat)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.snapchat}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/brands/snapchaticon.png')}
                          />
                          <AppText>Snapchat: Not set</AppText>
                        </>
                  }
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.facebook && communityMembership.profile.facebook.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getFacebookLink(communityMembership.profile.facebook)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/brands/facebookicon.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getFacebookLink(communityMembership.profile.facebook)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.facebook}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/brands/facebookicon.png')}
                          />
                          <AppText>Facebook: Not set</AppText>
                        </>
                  }
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  {
                    communityMembership.profile.x && communityMembership.profile.x.length > 0 ?
                        <>
                          <ExternalLink href={AppUtils.getXLink(communityMembership.profile.x)} target='_blank' className='nontext'>
                            <Image
                              style={AppConstants.STYLES.brandIcon}
                              source={require('@/assets/images/brands/xicon.png')}
                            />
                          </ExternalLink>
                          <ExternalLink href={AppUtils.getXLink(communityMembership.profile.x)} target='_blank' style={CustomTextStyles.link}>{communityMembership.profile.x}</ExternalLink>
                        </>
                      :
                        <>
                          <Image
                            style={styles.brandImage}
                            source={require('@/assets/images/brands/xicon.png')}
                          />
                          <AppText>X: Not set</AppText>
                        </>
                  }
                </View>
              </View>
              <View style={AppConstants.STYLES.padding2} />
              <CustomText>Optional settings</CustomText>
              <View style={styles.indent}>
                <AppText>Optional info to show on your profile:</AppText>
                <View style={styles.flexRowGapVerticalCenter}>
                  <AppText><AppText style={CustomTextStyles.highlight}>Ethnicity</AppText>: { getEthnicities(communityMembership.profile) }</AppText>
                  <Link
                    href={{ pathname: '/ethnicity', params: { edit: "y" } }}
                    asChild={AppUtilities.isWeb()}
                  >
                    <Image
                      style={styles.editImage}
                      source={require('@/assets/images/gears32x32.png')}
                    />
                  </Link>
                </View>
                <View style={styles.flexRowGapVerticalCenter}>
                  <AppText><AppText style={CustomTextStyles.highlight}>Religion</AppText>: { getReligions(communityMembership.profile) }</AppText>
                  <Link
                    href={{ pathname: '/religion', params: { edit: "y" } }}
                    asChild={AppUtilities.isWeb()}
                  >
                    <Image
                      style={styles.editImage}
                      source={require('@/assets/images/gears32x32.png')}
                    />
                  </Link>
                </View>
              </View>
            </View>

            <CustomDivider fullWidth={true} />
            <CustomText>Hide/unhide profile</CustomText>
            <View style={styles.indent}>
              {
                !communityMembership.hidden ?
                    <>
                      <AppText>Your profile is currently <AppText style={CustomTextStyles.highlight}>visible</AppText> to other members of this Rabbl.</AppText>
                      <AppText>You may hide your profile for this <AppText style={CustomTextStyles.highlight}>{communityMembership.community?.name}</AppText> Rabbl so that others can't see you in search results. You can still continue to chat with existing matches and they can still chat with you. You can still search for matches, but you can't like any new people or start new matches.</AppText>
                    </>
                  :
                    <>
                      <AppText>Your profile is currently <AppText style={CustomTextStyles.highlight}>hidden</AppText>. You can still continue to chat with existing matches and they can still chat with you. You can still search for matches, but you can't like any new people or start new matches.</AppText>
                    </>
              }
              <View style={AppConstants.STYLES.flexRow}>
                <Button onPress={onChangeHidden}>{communityMembership.hidden ? "Unhide profile" : "Hide profile"}</Button>
              </View>
            </View>

            {
              !communityMembership.hidden && (
                <>
                  <CustomDivider fullWidth={true} />
                  <CustomText>Public profile</CustomText>
                  <AppText>In addition to other members of this Rabbl being able to see your profile (see above), you may also have a public profile with your profile teaser on top and a page to join the Rabbl.</AppText>
                  <View style={styles.indent}>
                    <CustomText style={CustomTextStyles.medium}>Public profile settings</CustomText>
                    <View style={styles.indent}>
                      <View style={styles.flexRowGapVerticalCenter}>
                        <AppText><AppText style={CustomTextStyles.highlight}>Show public profile</AppText>: { communityMembership.hidepublicprofile ? "No" : "Yes" }</AppText>
                        <Link
                          href={{ pathname: '/profilesettings', params: { edit: "y" } }}
                          asChild={AppUtilities.isWeb()}
                        >
                          <Image
                            style={styles.editImage}
                            source={require('@/assets/images/gears32x32.png')}
                          />
                        </Link>
                      </View>
                      {
                        !communityMembership.hidepublicprofile && (
                          <>
                            <View style={styles.flexRowGapVerticalCenter}>
                              <AppText><AppText style={CustomTextStyles.highlight}>Show age</AppText>: { communityMembership.user_info.publicage ?? false ? "Yes" : "No" }</AppText>
                              <Link
                                href={{ pathname: '/profilesettings', params: { edit: "y" } }}
                                asChild={AppUtilities.isWeb()}
                              >
                                <Image
                                  style={styles.editImage}
                                  source={require('@/assets/images/gears32x32.png')}
                                />
                              </Link>
                            </View>
                          </>
                        )
                      }
                    </View>
                    {
                      !communityMembership.hidepublicprofile && (
                        <>
                          <CustomText style={CustomTextStyles.medium}>Public profile link</CustomText>
                          <View style={styles.indent}>
                            <View style={styles.flexRowGapVerticalCenter}>
                              <AppText><AppText style={CustomTextStyles.highlight}>Customize</AppText>: { AppUtils.getPublicProfileLinkFragment(communityMembership) }</AppText>
                              <Link
                                href={{ pathname: '/profilesettings', params: { edit: "y" } }}
                                asChild={AppUtilities.isWeb()}
                              >
                                <Image
                                  style={styles.editImage}
                                  source={require('@/assets/images/gears32x32.png')}
                                />
                              </Link>
                            </View>
                            <ExternalLink href={getMemberLink()} target='_blank' style={CustomTextStyles.link}>{getMemberLink()}</ExternalLink>
                            <View style={styles.buttonRow}>
                              {
                                !AppUtilities.isWeb() && (
                                  <Button onPress={share}>Share</Button>
                                )
                              }
                              <Button onPress={copyMemberLinkToClipboard}>Copy to clipboard</Button>
                            </View>
                          </View>
                          <CustomText style={CustomTextStyles.medium}>Website embed</CustomText>
                          <View style={styles.indent}>
                            <AppText>Embed HTML into a website to link to this Rabbl with your profile teaser on top:</AppText>
                            <View style={{ flexDirection: "row"}}>
                              <RabblSearchResult
                                community={communityMembership.community ?? UNKNOWN_COMMUNITY}
                                community_membership={communityMembership}
                                communityNameOverTop={true}
                                communityNameUnderneath={false}
                                showAge={false}
                                communityMembershipLink={true}
                              />
                            </View>
                            <View style={AppConstants.STYLES.flexRow}><Button onPress={() => setShowHTML(!showHTML)}>{showHTML ? "Hide HTML" : "Show HTML"}</Button></View>
                            { showHTML && (
                                <View style={styles.embedContainer}>
                                  <View style={styles.pickerWrapper}>
                                    <Picker selectedValue={embedType} onValueChange={setEmbedType} style={styles.picker}>
                                      <Picker.Item label="Iframe (recommended)" value="1" />
                                      {/*<Picker.Item label="Raw HTML" value="2" />*/}
                                    </Picker>
                                  </View>
                                  {
                                    embedType == 1 ?
                                        <AppText style={[AppConstants.STYLES.monospace, styles.indent]}>
                                          {getEmbedIframe()}
                                        </AppText>
                                      :
                                        embedType == 2 ?
                                            <AppText style={[AppConstants.STYLES.monospace, styles.indent]}>
                                              {getEmbedHTML()}
                                            </AppText>
                                          :
                                            null
                                  }
                                  <View style={[AppConstants.STYLES.flexRow, styles.indent]}><Button onPress={copyEmbedToClipboard}>Copy to clipboard</Button></View>
                                </View>
                            )}
                          </View>
                        </>
                      )
                    }
                  </View>
                </>
              )
            }
          </>
        )
      }

      {
        ((isMainLogicalHost && communityMemberships.length > 0) || communityMemberships.length > 1) && (
          <>
            <CustomDivider fullWidth={true} />
            <UserRabbls
              communityMemberships={communityMemberships}
              currentCommunityMembership={communityMembership}
              showDivider={false}
              align='flex-start'
              title={"Settings for the Rabbls you're in"}
              customLink={isMainLogicalHost ? "/settings" : undefined}
            />
          </>
        )
      }

      <CustomDivider fullWidth={true} />
      <CustomText>Password</CustomText>
      <View style={styles.indent}>
        <View style={AppConstants.STYLES.flexRow}>
          <Link href="/changepassword" asChild className={"nontext"}><Button>Change password</Button></Link>
        </View>
      </View>

      <CustomDivider fullWidth={true} />
      <CustomText>Delete account</CustomText>
      <View style={styles.indent}>
        {
          communityMemberships.length > 0 ?
              isMainLogicalHost ?
                  <>
                    <AppText>Instead of deleting your account, you can hide your profile in each Rabbl you're in.</AppText>
                    <AppText>If you want to completely delete your account and all pictures, matches, and user information, then select the delete button below. This is irreversible and applies to all Rabbls you've joined.</AppText>
                  </>
                :
                  <>
                    <AppText>Instead of deleting your account, you can hide your profile above.</AppText>
                    <AppText>If you want to completely delete your account and all pictures, matches, and user information, then select the delete button below. This is irreversible and applies to all Rabbls you've joined.</AppText>
                  </>
            :
              <>
                <AppText>If you want to completely delete your account, then select the delete button below. This is irreversible and applies to all your data.</AppText>
              </>
        }
        <View style={AppConstants.STYLES.flexRow}>
          <Link href="/deleteaccount" asChild className={"nontext"}><Button>Delete account</Button></Link>
        </View>
      </View>
      <Footer />
    </BasicScrollView>
  );
}
